import { type ImgHTMLAttributes, type PointerEvent, useCallback, useMemo, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { GalleryPhoto } from 'components/marketplace/helpers/galleryPhotos';
import type { GalleryBreakpointTier } from 'components/marketplace/helpers/presizedPhotos';
import { Carousel } from 'components/marketplace/shared/Carousel';
import { GalleryListingPhoto } from 'components/marketplace/shared/GalleryListingPhoto';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';

import { CardLink } from '../PropertyCard';
import { PDP_SIMILAR_NEXT_URL, PDP_SIMILAR_PREVIOUS_URL } from './refresh/media';
import { useMarketplaceCardPhotos } from './useMarketplaceCardPhotos';

const MAX_REFRESH_PHOTO_INDICATORS = 8;

const CARD_IMAGE_SIZES =
  '(min-width: 1201px) calc(25vw - 60px), (min-width: 768px) calc(50vw - 48px), calc(100vw - 88px)';

const CARD_BREAKPOINT_TIERS: GalleryBreakpointTier[] = [
  {
    minWidth: 0,
    maxWidth: null,
    preset: '3:2-xs',
    tier: 'xs',
    srcSetTiers: ['xs', 'sm', 'md', 'lg', 'xl'],
    imageSizes: CARD_IMAGE_SIZES,
    responsive: {
      sizes: CARD_IMAGE_SIZES,
      // Worker presets and Cabinet variants have different small-tier widths.
      presets: [
        { preset: '3:2-xs', width: 375 },
        { preset: '3:2-sm', width: 768 },
        { preset: '3:2-md', width: 1201 },
        { preset: '3:2-lg', width: 1440 },
        { preset: '3:2-xl', width: 1800 },
      ],
    },
  },
];

type PropertyCardGalleryProps = {
  variant?: 'default' | 'pdp-refresh';
  addressId: string;
  fetchPriority?: ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  href: string;
  isPhotoPending: boolean;
  openInNewTab?: boolean;
  /** Listing photos only; floor plans and walkthrough videos remain PDP media. */
  previewPhotoUrls: ReadonlyArray<string>;
  /** Explains the no-photo state, e.g. "Photos coming soon" for pre-listing homes. */
  placeholderLabel?: string;
};

const previewGalleryPhoto = (url: string): GalleryPhoto => ({
  url,
  urlXs: null,
  urlSm: null,
  urlLg: null,
  urlXl: null,
  isFloorPlan: false,
});

const EMPTY_GALLERY_PHOTOS: GalleryPhoto[] = [];
const MAX_VISIBLE_DOTS = 5;
const DOT_POSITION_CLASS_NAMES = [
  '-left-2',
  'left-1',
  'left-4',
  'left-7',
  'left-10',
  'left-[52px]',
  'left-16',
] as const;

const releasePointerFocus = (event: PointerEvent<HTMLButtonElement>) => {
  // Pointer clicks should not keep hover chrome visible after the pointer leaves the card.
  event.currentTarget.blur();
};

const DESKTOP_CONTROL_CLASS_NAME = cn(
  'pointer-events-none absolute top-1/2 z-[1] hidden size-11 -translate-y-1/2 border-0 bg-transparent p-0 opacity-0 shadow-none md:flex',
  'transition-opacity duration-150 motion-reduce:transition-none',
  'not-disabled:hover:border-transparent not-disabled:hover:bg-transparent',
  'not-disabled:active:border-transparent not-disabled:active:bg-transparent',
  'disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-transparent',
  'focus-visible:pointer-events-auto focus-visible:opacity-100',
  'group-focus-within/card-gallery:pointer-events-auto group-focus-within/card-gallery:opacity-100',
  'group-hover/card-gallery:pointer-events-auto group-hover/card-gallery:opacity-100',
);

type CardCarouselDotsProps = {
  count: number;
  currentIndex: number;
};

const CardCarouselDots = ({ count, currentIndex }: CardCarouselDotsProps) => {
  const firstVisibleIndex = Math.max(0, Math.min(currentIndex - 2, count - MAX_VISIBLE_DOTS));
  const visibleCount = Math.min(MAX_VISIBLE_DOTS, count);
  const firstRenderedIndex = Math.max(0, firstVisibleIndex - 1);
  const lastRenderedIndex = Math.min(count - 1, firstVisibleIndex + visibleCount);
  const renderedCount = lastRenderedIndex - firstRenderedIndex + 1;

  return (
    <Row className="items-center justify-center rounded-full bg-black/55 px-2 py-1">
      <Box className="relative h-2 w-14 overflow-hidden">
        {Array.from({ length: renderedCount }, (_, offset) => {
          const dotIndex = firstRenderedIndex + offset;
          const visibleOffset = dotIndex - firstVisibleIndex;
          const isVisible = visibleOffset >= 0 && visibleOffset < visibleCount;
          const isActive = dotIndex === currentIndex;
          const visibleClassName = isActive ? 'size-2 opacity-100' : 'size-1.5 opacity-60';

          return (
            <Box
              key={dotIndex}
              className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white transition-[left,width,height,opacity] duration-150 motion-reduce:transition-none ${DOT_POSITION_CLASS_NAMES[visibleOffset + 1]} ${
                isVisible ? visibleClassName : 'size-1.5 opacity-0'
              }`}
            />
          );
        })}
      </Box>
    </Row>
  );
};

export const PropertyCardGallery = ({
  variant = 'default',
  addressId,
  fetchPriority,
  href,
  isPhotoPending,
  openInNewTab = false,
  placeholderLabel,
  previewPhotoUrls,
}: PropertyCardGalleryProps) => {
  const isRefresh = variant === 'pdp-refresh';
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [index, setIndex] = useState(0);
  const [isFullGalleryRequested, setIsFullGalleryRequested] = useState(false);
  const { data: fullGalleryPhotos = EMPTY_GALLERY_PHOTOS, isSuccess } = useMarketplaceCardPhotos({
    addressId,
    enabled:
      isDesktop &&
      (isFullGalleryRequested || (isRefresh && !isPhotoPending && previewPhotoUrls.length > 0)),
  });

  const previewPhotos = useMemo(
    () => previewPhotoUrls.map(previewGalleryPhoto),
    [previewPhotoUrls],
  );
  const photos = useMemo(() => {
    // Keep the browse URLs mounted when the responsive gallery response arrives, and preserve
    // the browse response's display order.
    if (fullGalleryPhotos.length <= previewPhotos.length) {
      return previewPhotos;
    }
    return [...previewPhotos, ...fullGalleryPhotos.slice(previewPhotos.length)];
  }, [fullGalleryPhotos, previewPhotos]);
  const count = photos.length;
  const currentIndex = isDesktop ? Math.min(index, Math.max(0, count - 1)) : 0;
  const canWrap = isSuccess;

  const selectPhoto = useCallback((nextIndex: number) => {
    setIsFullGalleryRequested(true);
    setIndex(nextIndex);
  }, []);

  const requestFullGallery = useCallback(() => {
    setIsFullGalleryRequested(true);
  }, []);

  const renderSlide = useCallback(
    (photoIndex: number) => {
      const photo = photos[photoIndex];
      if (!photo) {
        return null;
      }
      return (
        <GalleryListingPhoto
          photo={photo}
          sizes={CARD_BREAKPOINT_TIERS}
          fetchPriority={photoIndex === 0 ? fetchPriority : undefined}
          alt={`Property photo ${photoIndex + 1}`}
          objectFit="cover"
          fit="cover"
        />
      );
    },
    [fetchPriority, photos],
  );

  const showPreviousPhoto = useCallback(() => {
    selectPhoto(canWrap ? (currentIndex - 1 + count) % count : Math.max(0, currentIndex - 1));
  }, [count, currentIndex, canWrap, selectPhoto]);

  const showNextPhoto = useCallback(() => {
    selectPhoto(canWrap ? (currentIndex + 1) % count : Math.min(count - 1, currentIndex + 1));
  }, [count, currentIndex, canWrap, selectPhoto]);

  if (isPhotoPending || count === 0) {
    return (
      <CardLink
        href={href}
        external={openInNewTab}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        className="h-full w-full"
      >
        <PropertyPhotoImage
          alt="Main view of property"
          loading="lazy"
          containerClassName={cn(
            'w-full object-cover',
            isRefresh ? 'aspect-[303/226]' : 'aspect-[16/9]',
          )}
          className="h-full w-full object-cover"
          placeholderClassName="bg-od-bg-tertiary"
          placeholderLabel={placeholderLabel}
          isPending={isPhotoPending}
          src={null}
          fetchPriority={fetchPriority}
        />
      </CardLink>
    );
  }

  return (
    <Box
      className="w-full"
      onFocus={isDesktop ? requestFullGallery : undefined}
      onPointerEnter={isDesktop ? requestFullGallery : undefined}
    >
      <Carousel
        count={count}
        index={currentIndex}
        onIndexChange={selectPhoto}
        ariaLabel="Property photos"
        className={cn(
          'group/card-gallery w-full bg-od-bg-secondary',
          isRefresh ? 'aspect-[303/226]' : 'aspect-[16/9]',
        )}
        renderSlide={renderSlide}
        interactive={isDesktop}
        windowSize={isDesktop && isFullGalleryRequested ? 1 : 0}
        wrap={canWrap}
      >
        <CardLink
          href={href}
          external={openInNewTab}
          ariaLabel={isDesktop ? 'View property details' : 'Main view of property'}
          analyticsName="marketplace-property-card-image-link"
          className="absolute inset-0"
        />
        {isDesktop && count > 1 && (
          <Button
            variant="secondary"
            aria-label="Previous photo"
            analyticsName="marketplace-property-card-carousel-previous"
            onClick={showPreviousPhoto}
            onPointerUp={releasePointerFocus}
            disabled={!canWrap && currentIndex === 0}
            className={
              isRefresh
                ? 'absolute left-3 top-1/2 z-1 size-7 -translate-y-1/2 rounded-full border-0 bg-transparent p-0 shadow-none not-disabled:hover:bg-transparent disabled:not-aria-busy:bg-transparent'
                : cn('left-1', DESKTOP_CONTROL_CLASS_NAME)
            }
          >
            <>
              {isRefresh ? (
                <Image src={PDP_SIMILAR_PREVIOUS_URL} width={28} height={28} alt="" aria-hidden />
              ) : (
                <IconChevronLeft color="white" size={28} stroke={2.5} className="drop-shadow-md" />
              )}
            </>
          </Button>
        )}
        {isDesktop && count > 1 && (
          <Button
            variant="secondary"
            aria-label="Next photo"
            analyticsName="marketplace-property-card-carousel-next"
            onClick={showNextPhoto}
            onPointerUp={releasePointerFocus}
            disabled={!canWrap && currentIndex === count - 1}
            className={
              isRefresh
                ? 'absolute right-3 top-1/2 z-1 size-7 -translate-y-1/2 rounded-full border-0 bg-transparent p-0 shadow-none not-disabled:hover:bg-transparent disabled:not-aria-busy:bg-transparent'
                : cn('right-1', DESKTOP_CONTROL_CLASS_NAME)
            }
          >
            <>
              {isRefresh ? (
                <Image src={PDP_SIMILAR_NEXT_URL} width={28} height={28} alt="" aria-hidden />
              ) : (
                <IconChevronRight color="white" size={28} stroke={2.5} className="drop-shadow-md" />
              )}
            </>
          </Button>
        )}
        {isRefresh && isDesktop && count > 1 && isSuccess && (
          <Row
            role="img"
            aria-label={`Photo ${currentIndex + 1} of ${count}`}
            className="pointer-events-none absolute bottom-5 inset-x-0 z-1 h-1 items-end justify-center gap-1"
          >
            {Array.from({ length: Math.min(count, MAX_REFRESH_PHOTO_INDICATORS) }, (_, i) => (
              <Box
                key={i}
                className={cn(
                  'w-3 rounded-sm bg-white',
                  i === Math.min(currentIndex, MAX_REFRESH_PHOTO_INDICATORS - 1) ? 'h-1' : 'h-0.5',
                )}
              />
            ))}
          </Row>
        )}
        {!isRefresh && isDesktop && count > 1 && canWrap && (
          <Box
            className="pointer-events-none absolute bottom-2 left-0 right-0 z-[1] hidden items-center justify-center opacity-0 transition-opacity duration-150 group-focus-within/card-gallery:opacity-100 group-hover/card-gallery:opacity-100 motion-reduce:transition-none md:flex"
            aria-hidden="true"
          >
            <CardCarouselDots count={count} currentIndex={currentIndex} />
          </Box>
        )}
      </Carousel>
    </Box>
  );
};
